import { useEffect, useState } from 'react';
import { api, type InvoiceHistory, type InvoiceHistoryFilter } from '../api';
import { label, useDebounced } from '../lib';

// The Invoice Status History Log — Adobe's correction_invoice_history_listing: every status change of
// every invoice (1.17M rows in the June copy), newest first, 50 a page, paged on the server. Keyed and
// packaged dates are the invoice's first change to that status; keyed-to-packaged is the days between,
// rounded up. The count stops at 10,000: narrow the filters to reach further back.
const STATUSES = ['keyed', 'review', 'review_edi', 'hold_for_follow_up', 'released_to_client', 'client_review',
  'client_returned', 'has_pdf', 'pdf_skipped', 'approved', 'packaged'];
const TEXT_FILTERS: Array<[keyof InvoiceHistoryFilter, string, string]> = [
  ['invoice', 'Invoice #', 'contains…'], ['po', 'PO #', 'contains…'], ['vendor', 'Vendor', 'name contains…'],
  ['customer', 'Customer', 'camp contains…'], ['creator', 'Creator', 'email or Cronjob'], ['comment', 'Comment', 'contains…'],
];
const when = (s: string | null) => (s ? s.slice(0, 16) : '—');

export function InvoiceHistoryLog({ onOpen }: { onOpen: (invoiceId: number) => void }) {
  const [f, setF] = useState<InvoiceHistoryFilter>({ sort: 'desc' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState<InvoiceHistory | null>(null);
  const [err, setErr] = useState('');
  const filter = useDebounced(f, 300);

  useEffect(() => {
    let live = true;
    setData(null); setErr('');
    api.commerceInvoiceHistory(filter, page).then((d) => live && setData(d)).catch((e) => live && setErr((e as Error).message));
    return () => { live = false; };
  }, [filter, page]);

  const set = (patch: InvoiceHistoryFilter) => { setF({ ...f, ...patch }); setPage(1); };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.per)) : 1;
  const any = Object.entries(f).some(([k, v]) => k !== 'sort' && v);

  return (
    <div className="stack">
      <section className="card">
        <div className="form-row hist-filters">
          {TEXT_FILTERS.map(([k, name, ph]) => (
            <label key={k} className="field"><span>{name}</span>
              <input className="search" placeholder={ph} value={(f[k] as string | undefined) ?? ''} onChange={(e) => set({ [k]: e.target.value })} />
            </label>
          ))}
          <label className="field"><span>Old status</span>
            <select className="sel" value={f.oldStatus ?? ''} onChange={(e) => set({ oldStatus: e.target.value })}>
              <option value="">Any</option>
              {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </label>
          <label className="field"><span>New status</span>
            <select className="sel" value={f.newStatus ?? ''} onChange={(e) => set({ newStatus: e.target.value })}>
              <option value="">Any</option>
              {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </label>
          <label className="field"><span>Logged from</span>
            <input className="search" type="date" value={f.from ?? ''} onChange={(e) => set({ from: e.target.value })} />
          </label>
          <label className="field"><span>to</span>
            <input className="search" type="date" value={f.to ?? ''} onChange={(e) => set({ to: e.target.value })} />
          </label>
          <label className="field"><span>Order</span>
            <select className="sel" value={f.sort ?? 'desc'} onChange={(e) => set({ sort: e.target.value as 'asc' | 'desc' })}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </label>
          {any && <button className="btn-sm ghost" onClick={() => { setF({ sort: f.sort }); setPage(1); }}>Clear filters</button>}
        </div>
      </section>
      {err && <p className="msg bad">⚠ {err}</p>}
      <section className="card">
        <div className="table-wrap">
          <table className="hist-table">
            <thead><tr>
              <th>Invoice #</th><th>PO #</th><th>Vendor</th><th>Customer</th><th>Creator</th><th>Old status</th><th>New status</th>
              <th>Comment</th><th>Log date</th><th>Keyed</th><th>Packaged</th><th className="num">Keyed → packaged (days)</th>
            </tr></thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} className={r.invoice_id ? 'row-click' : undefined} onClick={() => r.invoice_id && onOpen(r.invoice_id)}>
                  <td className="mono">{r.invoice_no ?? '—'}</td>
                  <td className="mono muted">{r.po ?? '—'}</td>
                  <td>{r.vendor ?? '—'}</td>
                  <td className="muted">{r.customer ?? '—'}</td>
                  <td className="muted">{r.creator ?? '—'}</td>
                  <td>{r.old_status ? <span className="pill type">{label(r.old_status)}</span> : '—'}</td>
                  <td>{r.new_status ? <span className="pill type">{label(r.new_status)}</span> : '—'}</td>
                  <td className="hist-comment" title={r.comment ?? undefined}>{r.comment ?? '—'}</td>
                  <td className="mono">{when(r.created_at)}</td>
                  <td className="mono muted">{when(r.keyed_at)}</td>
                  <td className="mono muted">{when(r.packaged_at)}</td>
                  <td className="num">{r.days_to_packaged ?? '—'}</td>
                </tr>
              ))}
              {data && data.rows.length === 0 && <tr><td colSpan={12} className="loading">No status changes match.</td></tr>}
            </tbody>
          </table>
        </div>
        {!data && !err && <p className="loading">Loading…</p>}
        {data && (
          <div className="iw-pager">
            <button className="btn-sm ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Prev</button>
            <span className="muted">
              page {page} / {pages} · {data.capped ? 'over 10,000 changes — narrow the filters to reach further' : `${data.total.toLocaleString()} changes`}
            </span>
            <button className="btn-sm ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next →</button>
          </div>
        )}
      </section>
    </div>
  );
}
